import { useMemo } from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import {
  getDataFromProviderFilters,
  getProviderSectionDetails,
  providerGlobalFilters,
  getSelectedAreaOfConcernIds,
  sortSpecialtiesBySelection,
} from 'app/ProviderDetails/utils'
import { getParsedQuery } from 'utils/common'
import styles from './index.module.scss'
import { getHighlightingInfo } from 'components/app/ProviderSearch/utils'
import useAppSelector from 'hooks/useAppSelector'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'

type OtherSpecialtiesProps = {
  providerDetails: Provider
}

const OtherSpecialties: React.FC<OtherSpecialtiesProps> = ({
  providerDetails,
}) => {
  const router = useRouter()
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const { filters } = providerDetails

  // Get highlighting information from URL query parameters
  const { selectedSpecialtyIds } = getHighlightingInfo(router)

  // Get area of concern filters for specialty highlighting
  const areaOfConcernFilters =
    globalGroupedFilters[ProviderFilterTypes.AREA_OF_CONCERN] || []

  // Convert selected specialty IDs to names for highlighting
  const selectedSpecialtyNames = selectedSpecialtyIds
    .map((id) => areaOfConcernFilters.find((filter) => filter.id === id)?.value)
    .filter(Boolean)

  const approaches = getDataFromProviderFilters(
    filters,
    providerGlobalFilters.CounselingApproach
  )
  const { topSpecialities } = getProviderSectionDetails(providerDetails)

  const specialities = getDataFromProviderFilters(
    filters,
    providerGlobalFilters.AreaOfConcern
  )
  const otherSpecialities = specialities?.filter(
    (speciality) => speciality.priority === 2
  )

  // Get selected area of concern IDs from URL query (use parsed query for consistent array parsing)
  const selectedAreaOfConcernIds = getSelectedAreaOfConcernIds(
    getParsedQuery(router)
  )

  // Combine and sort specialties so selected ones appear first in exact URL order
  const orderedSpecialties = sortSpecialtiesBySelection(
    [...topSpecialities, ...otherSpecialities],
    selectedAreaOfConcernIds
  )

  const { orderedSpecialitiesText, approachesText } = useMemo(() => {
    const specialtyNames =
      orderedSpecialties?.map((speciality) => speciality.value) || []

    const hasMatch =
      selectedSpecialtyNames.length > 0 &&
      selectedSpecialtyNames.some((name) => specialtyNames.includes(name))

    let orderedSpecialitiesText

    if (!hasMatch) {
      orderedSpecialitiesText = specialtyNames.join(', ')
    } else {
      // Has match - move matching specialties to first position
      const matchingSpecialties = specialtyNames.filter((name) =>
        selectedSpecialtyNames.includes(name)
      )
      const otherSpecialties = specialtyNames.filter(
        (name) => !selectedSpecialtyNames.includes(name)
      )
      const reorderedSpecialties = [...matchingSpecialties, ...otherSpecialties]

      orderedSpecialitiesText = (
        <span>
          {reorderedSpecialties.map((name, index) => (
            <span key={index}>
              {selectedSpecialtyNames.includes(name) ? (
                <span className="~font-semibold">{name}</span>
              ) : (
                name
              )}
              {index < reorderedSpecialties.length - 1 ? ', ' : ''}
            </span>
          ))}
        </span>
      )
    }

    const approachesText = approaches
      ?.map((approach) => approach.value)
      .join(', ')

    return { orderedSpecialitiesText, approachesText }
  }, [orderedSpecialties, approaches, selectedSpecialtyNames])

  return (
    <ProviderSectionContainer
      heading={
        !!orderedSpecialties?.length ? 'Other areas of focus' : 'Approaches'
      }
      id={PROVIDER_SECTION_IDS.OTHER_SPECIALTIES}
    >
      <div className={styles.orderedSpecialities_container}>
        {!!orderedSpecialties?.length && orderedSpecialitiesText && (
          <div className={styles.orderedSpecialities_row}>
            <span
              className={classNames(
                styles.orderedSpecialities_title,
                '~font-gibson ~font-medium ~text-black ~text-lg'
              )}
            >
              Specialties
            </span>
            <span className="~font-gibson ~font-normal ~text-black ~text-base">
              {orderedSpecialitiesText}
            </span>
          </div>
        )}

        {!!approaches?.length && approachesText && (
          <div className={styles.orderedSpecialities_row}>
            <span
              className={classNames(
                styles.orderedSpecialities_title,
                '~font-gibson ~font-medium ~text-black ~text-lg'
              )}
            >
              Approaches
            </span>
            <span className="~font-gibson ~font-normal ~text-black ~text-base">
              {approachesText}
            </span>
          </div>
        )}
      </div>
    </ProviderSectionContainer>
  )
}

export default OtherSpecialties
